import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES, EDIT_URL, SHOW_HUE_EDIT } from "@/lib/api";
import { RevealLines } from "@/components/site/Reveal";
import { scrollToId } from "@/hooks/useLenis";

export default function Hero({ start, onStartProject }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-[#23221f]"
    >
      <motion.div className="absolute inset-0" style={{ y, scale }}>
        <img
          src={IMAGES.hero}
          alt="A contemporary coastal residence with floor-to-ceiling glass opening to the Gulf"
          className="h-full w-full object-cover"
        />
        {/* Restrained scrim for legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#141310]/75 via-[#141310]/20 to-[#141310]/15" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#141310]/35 to-transparent" />
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-14 flex flex-col justify-end pb-20 md:pb-24"
      >
        <div className="max-w-4xl">
          <h1 className="font-display font-light text-[#F5F3EE] leading-[0.98] tracking-[-0.01em] text-[12.5vw] sm:text-6xl lg:text-[5.6rem]">
            <RevealLines lines={["Homes with soul", "are never accidental."]} start={start} delay={0.25} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={start ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1.1, delay: 1.0, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base md:text-lg font-light text-[#F5F3EE]/80 leading-relaxed"
          >
            Boutique interior design rooted in timeless living, craftsmanship, and the beauty of the coast.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={start ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1.1, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-col sm:flex-row items-start gap-3"
          >
            <button
              data-testid="hero-start-project"
              onClick={onStartProject}
              className="w-full sm:w-auto text-[12px] tracking-[0.2em] uppercase px-9 py-4 bg-[#F5F3EE] text-[#23221f] hover:bg-[#9c7a4e] hover:text-[#F5F3EE] transition-colors duration-500"
            >
              Start a Project
            </button>
            <a
              data-testid="hero-explore-edit"
              href={EDIT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={`${SHOW_HUE_EDIT ? "" : "hidden "}w-full sm:w-auto text-[12px] tracking-[0.2em] uppercase px-9 py-4 border border-[#F5F3EE]/50 text-[#F5F3EE] hover:border-[#F5F3EE] hover:bg-[#F5F3EE]/5 transition-colors duration-500 text-center`}
            >
              Explore The Hue Edit
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.button
        onClick={() => scrollToId("#philosophy")}
        initial={{ opacity: 0 }}
        animate={start ? { opacity: 1 } : {}}
        transition={{ duration: 1, delay: 1.7 }}
        className="absolute bottom-8 right-6 md:right-14 z-10 flex items-center gap-3"
        aria-label="Scroll down"
        data-testid="hero-scroll-cue"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#F5F3EE]/70">Scroll</span>
        <motion.span
          className="block h-8 w-px bg-[#F5F3EE]/50"
          animate={{ scaleY: [0.3, 1, 0.3], originY: 0 }}
          transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.button>
    </section>
  );
}
